// ── Admin Dashboard ───────────────────────────────────────────────────
function AdminPage({ onToast }) {
  const [requests, setRequests]     = React.useState([]);
  const [users, setUsers]           = React.useState([]);
  const [inviteCodes, setInviteCodes] = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [error, setError]           = React.useState(null);
  const [tab, setTab]               = React.useState("requests");
  const [processing, setProc]       = React.useState(null);
  const [generatingCodes, setGeneratingCodes] = React.useState(false);

  React.useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true); setError(null);
    try {
      const [{ data: reqs, error: e1 }, { data: roles, error: e2 }, { data: codes, error: e3 }] = await Promise.all([
        window.sbClient.from("access_requests").select("*").order("created_at", { ascending: false }),
        window.sbClient.from("user_roles").select("*").order("created_at", { ascending: false }),
        window.sbClient.from("invite_codes").select("*").order("created_at", { ascending: false }),
      ]);
      if (e1 || e2 || e3) throw (e1 || e2 || e3);
      setRequests(reqs || []);
      setUsers(roles || []);
      setInviteCodes(codes || []);
    } catch (e) {
      setError(e);
      setRequests([]); setUsers([]); setInviteCodes([]);
    }
    setLoading(false);
  }

  async function generateCodes() {
    setGeneratingCodes(true);
    try {
      const newCodes = Array.from({ length: 5 }, () => {
        const arr = new Uint8Array(4);
        crypto.getRandomValues(arr);
        return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
      });
      const expiresAt = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      const rows = newCodes.map(code => ({ code, expires_at: expiresAt }));
      const { error } = await window.sbClient.from("invite_codes").insert(rows);
      if (error) throw error;
      onToast?.(`Generated ${newCodes.length} invite codes`);
      fetchAll();
    } catch (e) {
      onToast?.(`Failed to generate codes: ${e.message}`);
    }
    setGeneratingCodes(false);
  }

  async function approve(req) {
    setProc(req.id);
    try {
      const { data: { user } } = await window.sbClient.auth.getUser();
      const r1 = await window.sbClient.from("access_requests")
        .update({ status:"approved", reviewed_by:user?.id, reviewed_at:new Date().toISOString() }).eq("id", req.id);
      if (r1.error) throw r1.error;
      if (req.user_id) {
        const r2 = await window.sbClient.from("user_roles").update({ role:"user" }).eq("user_id", req.user_id);
        if (r2.error) throw r2.error;
      }
      onToast?.(`✓ ${req.full_name} approved`);
    } catch (e) {
      onToast?.(`Approve failed: ${e.message}`);
    }
    setProc(null); fetchAll();
  }

  async function reject(req) {
    setProc(req.id);
    try {
      const { data: { user } } = await window.sbClient.auth.getUser();
      const { error } = await window.sbClient.from("access_requests")
        .update({ status:"rejected", reviewed_by:user?.id, reviewed_at:new Date().toISOString() }).eq("id", req.id);
      if (error) throw error;
      onToast?.(`Rejected ${req.full_name}`);
    } catch (e) {
      onToast?.(`Reject failed: ${e.message}`);
    }
    setProc(null); fetchAll();
  }

  async function changeRole(userId, role) {
    setProc(userId);
    try {
      const { error } = await window.sbClient.from("user_roles").update({ role }).eq("user_id", userId);
      if (error) throw error;
      onToast?.(`Role updated → ${role}`);
    } catch (e) {
      onToast?.(`Role update failed: ${e.message}`);
    }
    setProc(null); fetchAll();
  }

  const pending  = requests.filter(r => r.status === "pending");
  const approved = requests.filter(r => r.status === "approved");
  const active   = users.filter(u => u.role === "user" || u.role === "admin");

  const statusColor = { pending:"var(--yellow)", approved:"var(--green)", rejected:"var(--red)" };
  const roleColor   = { admin:"var(--primary)", user:"var(--green)", pending:"var(--yellow)" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr icon={Shield} iconColor="var(--red)" iconBg="var(--red-dim)"
        title="Admin dashboard"
        subtitle="Manage user access requests and roles"
        action={
          <button className="btn btn-ghost btn-sm" onClick={fetchAll}>
            <RefreshCw size={12}/> Refresh
          </button>
        }
      />

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
        <StatCard title="Pending"       value={pending.length}  icon={Clock}   accent="default"/>
        <StatCard title="Approved"      value={approved.length} icon={Check}   accent="green"/>
        <StatCard title="Active users"  value={active.length}   icon={Users}   accent="purple"/>
        <StatCard title="Total requests"value={requests.length} icon={FileText}accent="default"/>
        <StatCard title="Invite codes"  value={inviteCodes.filter(c=>!c.used_by).length} icon={Key} accent="default"/>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:0, borderBottom:"1px solid var(--border)" }}>
        {[["requests","Access Requests",pending.length],["users","Manage Users",null],["invites","Invite Codes",null]].map(([id,label,count])=>(
          <button key={id} onClick={()=>setTab(id)} style={{
            background:"none", border:"none", borderBottom:`2px solid ${tab===id?"var(--primary)":"transparent"}`,
            padding:"8px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:6,
            fontFamily:"var(--font-ui)", fontSize:13, fontWeight:tab===id?600:400,
            color:tab===id?"var(--primary)":"var(--muted)", transition:"all .15s",
          }}>
            {label}
            {count > 0 && <span style={{ background:"var(--red)", color:"#fff", fontFamily:"var(--font-mono)", fontSize:10, borderRadius:"var(--r-full)", padding:"1px 6px" }}>{count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonList rows={4}/>
      ) : error ? (
        <div className="card" style={{ padding:0 }}>
          <ErrorState
            title="Couldn't load admin data."
            body="Check that the access_requests and user_roles tables exist and your role has admin privileges."
            detail={error?.message}
            onRetry={fetchAll}
          />
        </div>
      ) : tab === "requests" ? (
        <div className="card" style={{ padding:0, overflow:"hidden" }}>
          {requests.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="Inbox zero."
              body="No access requests have come in yet. New requests from the marketing site will appear here."
            />
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid var(--border)" }}>
                  {["Name","Email","Company","Reason","Status","Date","Actions"].map(h=>(
                    <th key={h} style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"10px 14px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:i<requests.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                    <td style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:500, color:"var(--ink)", padding:"12px 14px" }}>{r.full_name}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"12px 14px" }}>{r.email}</td>
                    <td style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", padding:"12px 14px" }}>{r.company||"—"}</td>
                    <td style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", padding:"12px 14px", maxWidth:200 }}>
                      <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.reason}</div>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span className="badge" style={{ color:statusColor[r.status]||"var(--muted)", background:`${statusColor[r.status]}18`||"transparent", borderColor:`${statusColor[r.status]}30` }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--subtle)", padding:"12px 14px" }}>{r.created_at?.slice(0,10)}</td>
                    <td style={{ padding:"12px 14px" }}>
                      {r.status === "pending" && (
                        <div style={{ display:"flex", gap:6 }}>
                          <button className="btn btn-ghost btn-sm" style={{ color:"var(--green)", borderColor:"rgba(17,255,153,.25)" }} disabled={processing===r.id} onClick={()=>approve(r)}>
                            <UserCheck size={11}/> Approve
                          </button>
                          <button className="btn btn-danger btn-sm" disabled={processing===r.id} onClick={()=>reject(r)}>
                            <UserX size={11}/>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === "users" ? (
        <div className="card" style={{ padding:0, overflow:"hidden" }}>
          {users.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No users yet."
              body="Once members sign up and have a role assigned, they'll be listed here."
            />
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid var(--border)" }}>
                  {["User ID","Role","Joined","Change role"].map(h=>(
                    <th key={h} style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"10px 14px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u,i) => (
                  <tr key={u.id} style={{ borderBottom:i<users.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"12px 14px" }}>{u.user_id?.slice(0,12)}…</td>
                    <td style={{ padding:"12px 14px" }}>
                      <span className="badge" style={{ color:roleColor[u.role]||"var(--muted)", background:`${roleColor[u.role]}18`, borderColor:`${roleColor[u.role]}30` }}>
                        {u.role === "admin" && "★ "}{u.role}
                      </span>
                    </td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--subtle)", padding:"12px 14px" }}>{u.created_at?.slice(0,10)}</td>
                    <td style={{ padding:"12px 14px" }}>
                      <select value={u.role} disabled={processing===u.user_id} onChange={e=>changeRole(u.user_id,e.target.value)} style={{ background:"var(--bg-deep)", border:"1px solid var(--border-strong)", borderRadius:"var(--r-md)", padding:"4px 8px", fontFamily:"var(--font-ui)", fontSize:12, color:"var(--ink)", cursor:"pointer", outline:"none" }}>
                        <option value="admin">admin</option>
                        <option value="user">user</option>
                        <option value="pending">pending</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        /* ── Invite Codes tab ── */
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <button className="btn btn-primary btn-sm" disabled={generatingCodes} onClick={generateCodes}>
              <Key size={12}/> {generatingCodes ? "Generating…" : "Generate 5 codes"}
            </button>
          </div>
          <div className="card" style={{ padding:0, overflow:"hidden" }}>
            {inviteCodes.length === 0 ? (
              <EmptyState
                icon={Key}
                title="No invite codes yet."
                body="Click 'Generate 5 codes' to create new invite codes, or use the gen-invite-codes.mjs script."
              />
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid var(--border)" }}>
                    {["Code","Status","Used by","Expires","Created"].map(h=>(
                      <th key={h} style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"10px 14px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inviteCodes.map((c,i) => {
                    const used = !!c.used_by;
                    const expired = c.expires_at && new Date(c.expires_at) < new Date();
                    return (
                      <tr key={c.id} style={{ borderBottom:i<inviteCodes.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <td style={{ fontFamily:"var(--font-mono)", fontSize:13, fontWeight:600, color:"var(--ink)", padding:"12px 14px", letterSpacing:"0.05em" }}>{c.code}</td>
                        <td style={{ padding:"12px 14px" }}>
                          {used ? (
                            <span className="badge" style={{ color:"var(--subtle)", background:"rgba(255,255,255,0.06)", borderColor:"rgba(255,255,255,0.1)" }}>Used</span>
                          ) : expired ? (
                            <span className="badge" style={{ color:"var(--red)", background:"var(--red-dim)", borderColor:"rgba(255,80,80,0.25)" }}>Expired</span>
                          ) : (
                            <span className="badge" style={{ color:"var(--green)", background:"rgba(17,255,153,0.08)", borderColor:"rgba(17,255,153,0.25)" }}>Unused</span>
                          )}
                        </td>
                        <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"12px 14px" }}>{c.used_by ? c.used_by.slice(0,12)+"…" : "—"}</td>
                        <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color: expired?"var(--red)":"var(--subtle)", padding:"12px 14px" }}>{c.expires_at?.slice(0,10) || "—"}</td>
                        <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--subtle)", padding:"12px 14px" }}>{c.created_at?.slice(0,10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
window.AdminPage = AdminPage;
